<script setup lang="ts">
import { SocialReviewFeedProcessedCard, SocialProcessFlowCardResult } from '../SocialReviewFeed.vue';
import { useSocialStore } from '@Portal/stores/social';
import { getImageUrl } from '@Shared/components/visuals/utility';
import ReviewMatchedIcon from '@Shared/components/visuals/visualAssets/social/ReviewMatchedIcon.svg?component';
import NpsPromoter from '@Shared/components/visuals/visualAssets/NpsPromoter.svg?component';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import isToday from 'dayjs/plugin/isToday';
import { DATETIME_FORMAT } from '@Shared/constants';
dayjs.extend(isoWeek);
dayjs.extend(isToday);

const props = defineProps<{
  card: SocialReviewFeedProcessedCard;
}>();

const { card } = toRefs(props);

const calculateRelativeTime = (card: SocialProcessFlowCardResult): string => {
  if (!card.reviewPostedAt) return '';

  const date = dayjs(card.reviewPostedAt);
  return date.from(dayjs(import.meta.env.VITE_DATE_ANCHOR));
};

const verbatim = ref<Element>();
const verbatimIsTruncated = ref<boolean>(false);
const renderShowMoreButton = computed<boolean>(() => showingMore.value || verbatimIsTruncated.value);
const showingMore = ref<boolean>(false);
const socialStore = useSocialStore();
onMounted(() => {
  // We want to detect if a card's verbatim is too large to fit (so we can show a 'show more' button)
  // if the scrollHeight ("true height"/that can be scrolled) is greater than the visible height of the box
  // then we have something that's too large.
  const observer = new ResizeObserver(function (entries) {
    for (const entry of entries) {
      const el = entry.target;
      verbatimIsTruncated.value = !!el && el.scrollHeight > entry.contentRect.height + 2; // sometimes browser will add 1px to scrollHeight
    }
  });

  observer.observe(verbatim.value!);
});

const onCta = () => {
  let sentimentLabel;
  if (card.value.ratingIndex < 0) sentimentLabel = 'Negative';
  else if (card.value.ratingIndex > 0) sentimentLabel = 'Positive';
  else sentimentLabel = 'Neutral';
  socialStore.trackEvent('Call to action', { primaryMetricSentimentLabel: sentimentLabel });
  window.open(card.value.callToActionUrl);
};

const onMatchedSurveyResponseClick = () => {
  window.open(card.value.processFlowLink);
};
</script>

<template>
  <div
    class="vue-review-card"
    :class="{
      'review-positive': card.ratingIndex > 0,
      'review-negative': card.ratingIndex < 0,
      'review-neutral': card.ratingIndex == 0,
      'vue-review-card--matched': card.isMatched,
    }"
  >
    <div v-if="card.isMatched" class="vue-review-card-matched-details">
      <div class="vue-review-card-matched-details__banner">
        <ReviewMatchedIcon class="shrink-0" />
        <p id="ReviewsGeneratedBanner">
          This Google review was generated from a Resonate survey response through Review Amplifier
        </p>
      </div>

      <div class="vue-review-card-matched-details__content">
        <div class="vue-review-card-matched-details__content__icon-container">
          <NpsPromoter class="w-[29px] h-[29px]" />
        </div>
        <div>
          <div class="flex gap-x-2 flex-wrap">
            <p class="review-name">{{ card.contactTitle }}</p>
            <p class="review-date">{{ dayjs(card.surveyRespondedOn).format(DATETIME_FORMAT) }}</p>
          </div>
          <p class="mb-1">Resonate {{ card.programName }}</p>
          <div
            @click="onMatchedSurveyResponseClick"
            class="w-fit gap-[0.4rem] color-[#C73362] m-0 flex flex-row items-center cursor-pointer select-none"
          >
            View Details
            <div class="ml-1 i-mdi-chevron-right-circle text-3.5" />
          </div>
        </div>
      </div>
    </div>

    <div :class="['vue-review-card-header', card.isMatched ? 'vue-review-card-header--matched' : undefined]">
      <div class="review-star-section">
        <img
          class="review-star"
          v-for="(star, index) in Array(card.rating)"
          :key="index"
          :src="getImageUrl('/social/Star_Filled.svg')"
        />
        <img
          class="review-star"
          v-for="(star, index) in Array(5 - card.rating)"
          :key="index"
          :src="getImageUrl('/social/Star_Unfilled.svg')"
        />
      </div>
      <div class="review-details">
        <div class="review-details-top">
          <div class="review-name">{{ card.name }}</div>
          <div class="review-date">
            {{ card.isMatched ? dayjs(card.reviewPostedAt).format(DATETIME_FORMAT) : calculateRelativeTime(card) }}
          </div>
          <svg
            v-if="card.isUnread"
            class="unread-indicator"
            width="42"
            height="16"
            viewBox="0 0 42 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1.17647 0L41.0924 0C41.9452 0 42.3255 1.45528 41.6636 2.18599L37.2623 7.04478C36.8161 7.53733 36.8161 8.46267 37.2623 8.95522L41.6636 13.814C42.3255 14.5447 41.9452 16 41.0924 16H1.17647C0.526722 16 0 15.4733 0 14.8235V8V1.17647C0 0.526724 0.526722 0 1.17647 0Z"
              fill="#0D99FF"
            />
            <text font-size="8px" x="4" y="11" fill="white">Unread</text>
          </svg>
        </div>
        <div class="review-details-bottom">
          <div class="review-location" :class="{ 'card-unread': card.isUnread }">
            Google Reviews - {{ card.location }}
          </div>
        </div>
      </div>
    </div>
    <div class="vue-review-card-body">
      <span
        :class="{
          'empty-verbatim': !card.reviewComment,
          'show-more': !showingMore,
          'card-unread': card.isUnread,
        }"
        class="card-verbatim"
        ref="verbatim"
      >
        {{ card.reviewComment || 'No comment provided' }}
      </span>
      <a v-if="renderShowMoreButton" class="review-show-more" @click="showingMore = !showingMore">
        <svg
          v-if="!showingMore"
          width="13"
          height="8"
          viewBox="0 0 13 8"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M1 1.55566L6.5 6.44455L12 1.55566"
            stroke="#C73362"
            stroke-width="1.22222"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <svg v-if="showingMore" width="13" height="8" viewBox="0 0 13 8" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M1 6.44434L6.5 1.55545L12 6.44434"
            stroke="#C73362"
            stroke-width="1.22222"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        {{ showingMore ? 'Show Less' : 'Show More' }}
      </a>
      <div class="card-verbatim" :class="{ 'card-unread': card.isUnread }" v-if="card.reviewReply">
        <div class="review-subtitle-reply">Your reply:</div>
        {{ card.reviewReply }}
      </div>
      <a class="review-cta-button" @click="onCta">
        {{ card.callToAction }}
      </a>
    </div>
  </div>
</template>

<style scoped lang="scss">
.review-name {
  line-height: 12px;
  font-weight: 700;
  color: #4a4a4a;
  vertical-align: middle;
}

.review-date {
  line-height: 12px;
  font-weight: 600;
  color: #828282;
  vertical-align: middle;
}

.vue-review-card {
  border-radius: 3px;
  margin: 16px;
  border: 1px solid #bdbdbd;
  border-top: none;

  &:before {
    content: '';
    display: block;
    /* Margin equal to parent's border size so it overlaps */
    margin-left: -1px;
    margin-right: -1px;
    border-radius: 3px 3px 0px 0px;
  }

  &.review-positive:before {
    border-top: 4px solid #0c8a65;
  }

  &.review-negative:before {
    border-top: 4px solid #d4002b;
  }

  &.review-neutral:before {
    border-top: 4px solid #7f7466;
  }

  &--matched {
    border: 1px solid #bdbdbd;
    border-radius: 5px;
    overflow: hidden;

    &:before {
      content: none;
    }
  }

  .unread-indicator {
    margin-left: 8px;
    margin-top: auto; // to align with div
    margin-bottom: auto; // to align with div
    user-select: none;
  }

  .card-unread {
    font-weight: 600 !important;
  }

  .vue-review-card-matched-details {
    background-color: #f1fffb;
    border-bottom: 1px solid #bdbdbd;

    &__banner {
      display: flex;
      gap: 8px;
      align-items: center;
      padding-inline: 16px;
      padding-block: 8px;
      background-color: #0c8a65;

      color: white;
      font-size: 14px;
      line-height: 20px;
    }

    &__content {
      display: flex;
      font-size: 14px;
      padding-top: 15px;
      padding-bottom: 13px;
      padding-right: 15px;

      &__icon-container {
        width: 124px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }
  }

  .vue-review-card-header {
    display: flex;
    flex-direction: row;
    align-items: center;
    border-bottom: 1px solid #bdbdbd;

    .review-details {
      display: flex;
      flex-direction: column;
      line-height: 145%;
      font-size: 14px;
      margin-left: 22px;

      .review-details-top {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;

        padding-right: 15px;

        .review-date {
          margin-left: 8px;
        }
      }

      .review-details-bottom {
        .review-location {
          font-weight: 400;
          color: #4a4a4a;
        }
      }
    }

    .review-star-section {
      display: flex;
      margin-left: 22px;
      margin-top: 37px;
      margin-bottom: 33px;

      .review-star {
        margin-left: 2px;
        width: 14px;
        height: 14px;
      }
    }

    // Reviews Generated header is shorter and is visually merged with the body of the card (no border)
    &--matched {
      border: none;
      height: 60px;
      padding-top: 25px;

      .review-star-section {
        margin-top: 0px;
        margin-bottom: 0px;
      }
    }
  }

  .vue-review-card-body {
    padding-left: 124px;
    padding-top: 12px;
    padding-bottom: 24px;
    margin-right: 44px;

    .review-show-more {
      color: #c73362;
      font-weight: 400;
      font-size: 14px;
      line-height: 145%;
      cursor: pointer;
    }

    .card-verbatim {
      font-weight: 400;
      font-size: 14px;
      line-height: 20px;
      display: flex;
      flex-direction: column;

      &.show-more {
        // limit to 2 lines
        display: -webkit-box;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }

      &.empty-verbatim {
        font-style: italic;
      }
    }

    .review-subtitle-reply {
      margin-top: 16px;
      font-weight: 600;
      color: #4a4a4a;
    }

    .review-cta-button {
      display: block;
      padding: 8px 16px;
      color: white;
      background-color: #c73362;
      font-weight: 500;
      font-size: 14px;
      line-height: 19px;
      border: 1px solid #c73362;
      border-radius: 4px;
      max-width: fit-content;
      cursor: pointer;
      margin-top: 25px;

      &:hover {
        filter: drop-shadow(0px 0px 8px rgba(199, 51, 98, 0.75));
        border: solid 1px white;
      }
    }
  }
}

@media only screen and (max-width: 448px) {
  .vue-review-card {
    .unread-indicator {
      margin-left: 4px;
    }

    .vue-review-card-matched-details {
      &__banner {
        font-size: 10px;
        line-height: 16px;
      }
      &__content {
        font-size: 10px;

        &__icon-container {
          width: 87px;
        }
      }
    }

    .vue-review-card-header {
      .review-details {
        font-size: 10px;
        margin-left: 12px;

        .review-details-top {
          .review-date {
            margin-left: 4px;
          }
        }
      }

      .review-star-section {
        margin-left: 15px;
        margin-top: 33px;
        margin-bottom: 30px;

        .review-star {
          width: 10px;
          height: 10px;
        }
      }
    }

    .vue-review-card-body {
      padding-left: 83px;
      padding-top: 12px;
      padding-bottom: 16px;

      .card-verbatim {
        font-size: 10px;
        line-height: 16px;
      }

      .review-show-more {
        font-size: 10px;
      }

      .review-cta-button {
        font-size: 12px;
        line-height: 16px;
      }
    }
  }
}
</style>
